/**
 * IntegrationCard - Displays a configured integration with its summary and actions
 */

import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { cn } from '@/lib/utils'
import type { Integration, Language } from '@/types/integrations'
import { integrationDetails } from '@/lib/integrations/integrationDetails'
import { getLocalizedText, getCategoryColor, getConfigSummary } from '@/lib/integrations/integrationUtils'

interface IntegrationCardProps {
  integration: Integration
  language: Language
  onEdit: (integration: Integration) => void
  onDelete: (integration: Integration) => void
}

export function IntegrationCard({ integration, language, onEdit, onDelete }: IntegrationCardProps) {
  const { t } = useTranslation()

  if (!integration || !integration.name) {
    return null
  }

  const details = integrationDetails[integration.name]
  if (!details) {
    return null
  }

  const displayName = getLocalizedText(details.displayName, language)
  const category = getLocalizedText(details.category, language)
  const configSummary = getConfigSummary(integration, language)
  const categoryColor = getCategoryColor(category)

  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center">
        {/* Left: Icon, Name, Category */}
        <div className="flex shrink-0 items-center gap-3" style={{ minWidth: 200, width: 220 }}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center">
            <img
              src={details.iconPath}
              alt={displayName}
              className="h-8 w-8 object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {displayName}
            </h3>
            <span
              className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
              style={{ backgroundColor: categoryColor }}
            >
              {category}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 h-12 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Middle: Summary */}
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.assistants.integrations.summary')}
          </p>
          {configSummary.required.length === 0 && configSummary.optional.length === 0 ? (
            <p className="text-xs italic text-gray-500 dark:text-gray-400">
              {t('resources.assistants.integrations.no_config_set')}
            </p>
          ) : (
            <div className="flex gap-6">
              {/* Required Config Column */}
              {configSummary.required.length > 0 && (
                <div className="min-w-0 flex-1">
                  {configSummary.required.map((item, index) => (
                    <div
                      key={index}
                      className="flex min-w-0 items-center gap-1 overflow-hidden"
                      title={`${item.label} ${item.value}`}
                    >
                      <span className="shrink-0 text-xs leading-tight text-gray-500 dark:text-gray-400">
                        {item.label}
                      </span>
                      <span className="min-w-0 truncate text-xs leading-tight text-gray-900 dark:text-gray-100">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Optional Config Column */}
              {configSummary.optional.length > 0 && (
                <div className="min-w-0 flex-1">
                  {configSummary.optional.map((item, index) => (
                    <div
                      key={index}
                      className="flex min-w-0 items-center gap-1 overflow-hidden"
                      title={`${item.label} ${item.value}`}
                    >
                      <span className="shrink-0 text-xs leading-tight text-gray-500 dark:text-gray-400">
                        {item.label}
                      </span>
                      <span className="min-w-0 truncate text-xs leading-tight text-gray-900 dark:text-gray-100">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-4 h-12 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Right: Actions */}
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => onEdit(integration)}
            className="h-8 w-8 p-0"
            iconLeading={Pencil}
          />
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => onDelete(integration)}
            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            iconLeading={Trash2}
          />
        </div>
      </div>
    </div>
  )
}
